import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const ChannelTakeawaysDocument = graphql(/* GraphQL */ `
  query ChannelTakeaways($input: ChannelTakeawaysInput!) {
    channelTakeaways(input: $input) {
      id
      title
      description
      category
      channelIds
      feedback
      ctas {
        label
        action
        variant
      }
      metadata {
        draftId
        createPostType
        postId
        prefillAiPrompt
      }
    }
  }
`)

type TakeawaysData = ResultOf<
  typeof ChannelTakeawaysDocument
>['channelTakeaways']

interface UseChannelTakeawaysOptions {
  channelId: string
  organizationId: string
  /** Skip the query entirely (e.g. the actor can't view insights here). */
  skip?: boolean
}

interface UseChannelTakeawaysResult {
  data: TakeawaysData
  loading: boolean
}

export function useChannelTakeaways({
  channelId,
  organizationId,
  skip = false,
}: UseChannelTakeawaysOptions): UseChannelTakeawaysResult {
  // Apollo keeps the last result when a query becomes skipped, so the same flag
  // that skips the request must also gate what this hook reports. The page stays
  // mounted across a channel switch, so the previous channel's takeaways would
  // otherwise be read as this one's.
  const shouldSkip = skip || !channelId || !organizationId
  const { data, loading } = useQuery(ChannelTakeawaysDocument, {
    variables: {
      input: {
        channelId,
        organizationId,
      },
    },
    // Takeaways go stale from things that happen elsewhere — a draft published,
    // a post sent, a goal set — and the server drops and replaces them on read.
    // `cache-first` would serve the stale set on a client-side navigation and
    // never ask. Cached Takeaways still render immediately; the caller separates
    // "no data yet" from "refreshing", so this costs no skeleton.
    fetchPolicy: 'cache-and-network',
    skip: shouldSkip,
  })

  return {
    data: shouldSkip ? [] : (data?.channelTakeaways ?? []),
    loading,
  }
}
